import React, { useEffect, useState } from "react";

import TemporaryOverlay from "./TemporaryOverlay";
import { ButtonAndScoreboard } from "./Scoreboard";
import { ShotHistoryButton } from "./ShotHistory";
import { TeamLeaderButton } from "./TeamLeaderPanel";
import prose from "./prose";

import styles from "./BulletCount.module.css";

import medkit from "./images/art/medkit.png";
import bullet from "./images/art/bullet.png";
import armour from "./images/art/helmet.png";
import cross from "./images/cross.svg";
import { getGunImgFromUser } from "./utils";

const make_n_images = (n, image) =>
  Array(n)
    .fill()
    .map((_, i) => <img src={image} alt="" key={i} className={styles.icon} />);

// `onPickupAnimating` is told whether a pickup overlay is showing, so that
// UserMode can hold back the next `?d=` collection until it has played. The
// collector itself lives in UserMode rather than here, because this HUD is
// only shown to a living player and a medpack is for one who is not (#302).
export default function BulletCount({ user, onPickupAnimating = () => {} }) {
  const [previousUser, setPreviousUser] = useState(null);

  const [showBulletAnim, setShowBulletAnim] = useState(false);
  const [showArmourAnim, setShowArmourAnim] = useState(false);
  const [showMedpackAnim, setShowMedpackAnim] = useState(false);

  const [showGunPickup, setShowGunPickup] = useState(false);

  const anyActive = Boolean(
    showBulletAnim | showArmourAnim | showMedpackAnim | showGunPickup,
  );

  useEffect(() => {
    onPickupAnimating(anyActive);
  }, [anyActive, onPickupAnimating]);

  // Unmounting mid-animation (the player was knocked out) must not leave
  // collection held back for good.
  useEffect(() => () => onPickupAnimating(false), [onPickupAnimating]);

  useEffect(() => {
    var timeoutHandle = null;

    if (previousUser) {
      setShowBulletAnim(user.num_bullets > previousUser.num_bullets);
      setShowArmourAnim(
        user.hit_points > previousUser.hit_points &&
          previousUser.hit_points > 0,
      );
      setShowMedpackAnim(
        user.hit_points === 1 && previousUser.hit_points === 0,
      );

      const collectedGun =
        user.shot_damage !== previousUser.shot_damage ||
        user.shot_timeout !== previousUser.shot_timeout;
      setShowGunPickup(collectedGun);

      timeoutHandle = setTimeout(() => {
        setShowBulletAnim(false);
        setShowArmourAnim(false);
        setShowMedpackAnim(false);
        setShowGunPickup(false);
      }, 3000);
    }

    setPreviousUser(user);

    if (timeoutHandle) {
      return () => {
        clearTimeout(timeoutHandle);
      };
    }
  }, [user, previousUser]);

  return (
    <div id="bullet-count" className={styles.bulletCount}>
      <p>
        {prose.bulletCount.ammoLabel}{" "}
        {user.num_bullets > 0 ? (
          user.num_bullets > 3 ? (
            <>
              {make_n_images(1, bullet)} x{user.num_bullets}
            </>
          ) : (
            make_n_images(user.num_bullets, bullet)
          )
        ) : (
          make_n_images(1, cross)
        )}
      </p>
      {/* A player weighing up an appeal needs to know what they have left
          (roadmap R8), so it sits with the ammo rather than behind a tap */}
      <p>{prose.bulletCount.appealsRemaining(user.appeals_remaining)}</p>
      <p>
        {prose.bulletCount.armourLabel}{" "}
        {user.hit_points > 1
          ? make_n_images(user.hit_points - 1, armour)
          : make_n_images(1, cross)}
      </p>
      <p>
        <img
          className={styles.weaponImg}
          src={getGunImgFromUser(user)}
          alt=""
        />
      </p>

      <TemporaryOverlay img={bullet} appear={showBulletAnim} />
      <TemporaryOverlay img={armour} appear={showArmourAnim} />
      <TemporaryOverlay img={medkit} appear={showMedpackAnim} />
      <TemporaryOverlay img={getGunImgFromUser(user)} appear={showGunPickup} />

      <ButtonAndScoreboard />
      <ShotHistoryButton />
      <TeamLeaderButton user={user} />
    </div>
  );
}
